import React from 'react';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Button from '@amzn/meridian/button';
import Text from '@amzn/meridian/text';
import Alert from '@amzn/meridian/alert';
import Expander from '@amzn/meridian/expander';

import AttendanceOverride from './AttendanceOverride';
import AutomationConfiguration from './AutomationConfiguration';
import RateDistribution from './RateDistribution';
import { TEXTS } from '../../utils/constants';

// Collapsed by default like the other panels; editing happens in its own modal.
const PlanningWindowsSection = ({ planningWindows, onEdit }) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  return (
    <div className="fp-theme">
      <Expander open={isExpanded} onChange={setIsExpanded} title="Planning Windows" type="inline" spacingInset="300">
        <Column spacing="200" alignmentHorizontal="stretch" width="100%">
          <Row alignmentHorizontal="justify" alignmentVertical="center" width="100%">
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 16px' }}>
              {(planningWindows?.intervals || []).map((interval) => (
                <Text key={interval.windowName} type={TEXTS.T4}>
                  <strong>{interval.windowName}</strong> {interval.startTime}&ndash;{interval.endTime}
                </Text>
              ))}
              {!planningWindows?.exists && !planningWindows?.loading && (
                <Text type={TEXTS.T4} color="secondary">
                  No planning windows configured for this station.
                </Text>
              )}
            </div>
            <Button type="secondary" size="small" onClick={onEdit}>
              Edit
            </Button>
          </Row>
          {planningWindows?.lastUpdatedBy && (
            <Text type={TEXTS.T4} color="secondary">
              Last updated by {planningWindows.lastUpdatedBy}
            </Text>
          )}
        </Column>
      </Expander>
    </div>
  );
};

const GroupLabel = ({ title, hint }) => (
  <Row spacing="200" alignmentVertical="baseline">
    <Text type={TEXTS.T3}>{title}</Text>
    <Text type={TEXTS.T4} color="secondary">
      {hint}
    </Text>
  </Row>
);

// With a plan: panels edit `model`, Apply recalculates, Cancel restores the snapshot.
// Without one: automation only, saved to the station AUTOMATION_CONFIG file.
// Time Distributions is station-level, so it closes this modal rather than stacking.
export default function FlowPlannerSettingsModal({
  isOpen,
  onCancel,
  onApply,
  model,
  planVersion,
  stationAutomation,
  planningWindows,
  onOpenTimeDistributions,
  onAutomationConfigChange,
  onCycleAutomationConfigChange,
  onAttendanceChange,
  onMultipliersChange,
  onTargetRateChange,
  gridLayout = 'new'
}) {
  if (!isOpen) return null;

  const hasPlan = !!model;
  const automationConfig = hasPlan ? model.overrides.automationConfig : stationAutomation?.config;

  const footer = hasPlan ? (
    <Row spacing="200">
      <Button type="secondary" onClick={onCancel}>
        Cancel
      </Button>
      <Button type="primary" onClick={onApply}>
        Apply &amp; Calculate Plan
      </Button>
    </Row>
  ) : (
    <Row spacing="200">
      <Button type="secondary" onClick={onCancel}>
        Cancel
      </Button>
      {/* Save only writes the station automation config, so it is inert without one. */}
      <Button type="primary" disabled={stationAutomation?.saving || !stationAutomation?.exists} onClick={onApply}>
        {stationAutomation?.saving ? 'Saving...' : 'Save'}
      </Button>
    </Row>
  );

  return (
    <Modal
      title="Inputs"
      open={isOpen}
      onClose={onCancel}
      scrollContainer="viewport"
      closeLabel="Close"
      bodySpacingInset="400"
      width="90vw">
      <Column spacing="400" alignmentHorizontal="stretch" width="100%">
        {!hasPlan && stationAutomation?.error && (
          <Alert type="error" size="medium">
            {stationAutomation.error}
          </Alert>
        )}

        {hasPlan && (
          <Column spacing="200" alignmentHorizontal="stretch" width="100%">
            <GroupLabel title="Inputs" hint="Attendance and rates for this plan" />
            <div className="fp-results-panel">
              <div className="fp-results-section">
                <AttendanceOverride
                  laborAvailability={model.inputs.laborAvailability}
                  intervals={model.inputs.intervals}
                  attendanceOverrides={model.overrides.attendanceOverrides || {}}
                  onAttendanceChange={onAttendanceChange}
                  readOnly={planVersion === 'DYNAMIC_V2'}
                  showAttendanceRate={gridLayout === 'new'}
                  joined
                />
              </div>
              <div className="fp-results-section">
                <RateDistribution
                  availableCycles={model.inputs.availableCycles}
                  intervals={model.inputs.intervals}
                  processRates={model.inputs.processRates}
                  rateDistributions={model.overrides.rateDistributions}
                  packageSizeByInterval={model.inputs.packageSizeByInterval}
                  onMultipliersChange={onMultipliersChange}
                  onTargetRateChange={onTargetRateChange}
                  joined
                />
              </div>
            </div>
          </Column>
        )}

        <Column spacing="200" alignmentHorizontal="stretch" width="100%">
          <GroupLabel
            title="Configuration"
            hint={hasPlan ? 'Automation for this plan, and the station planning windows' : 'Station defaults'}
          />
          <div className="fp-results-panel">
            {/* Only mounted when there is a config to edit. Cycle-level plans can carry
                cycleAutomationConfig alone, so the condition mirrors the child's. */}
            {(automationConfig ||
              (hasPlan && model.overrides.isCycleLevelAutomation && model.overrides.cycleAutomationConfig)) && (
              <div className="fp-results-section">
                <AutomationConfiguration
                  automationConfig={automationConfig}
                  setAutomationConfig={onAutomationConfigChange}
                  availableCycles={hasPlan ? model.inputs.availableCycles : []}
                  cycleAutomationConfig={hasPlan ? model.overrides.cycleAutomationConfig : null}
                  isCycleLevelAutomation={hasPlan ? model.overrides.isCycleLevelAutomation : false}
                  onCycleAutomationConfigChange={onCycleAutomationConfigChange}
                  joined
                />
              </div>
            )}
            <div className="fp-results-section">
              <PlanningWindowsSection planningWindows={planningWindows} onEdit={onOpenTimeDistributions} />
            </div>
          </div>
        </Column>
      </Column>

      <ModalFooter>
        <Row alignmentHorizontal="justify" alignmentVertical="center" width="100%">
          <Text type={TEXTS.T4} color="secondary">
            {hasPlan
              ? 'Changes are discarded on Cancel and take effect when the plan is calculated.'
              : 'Saved for this station and used by the next plan you run.'}
          </Text>
          {footer}
        </Row>
      </ModalFooter>
    </Modal>
  );
}
